import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

interface Laylo {
  _id: string;
  apiKey?: string;
}

export function SettingsLaylo() {
  const [laylo, setLaylo] = useState<Laylo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreatingLaylo, setIsCreatingLaylo] = useState(false);
  const [isDeletingLaylo, setIsDeletingLaylo] = useState(false);
  const [isSavingLaylo, setIsSavingLaylo] = useState(false);
  const [showLayloApiKey, setShowLayloApiKey] = useState(false);
  const [layloApiKey, setLayloApiKey] = useState("");

  useEffect(() => {
    async function fetchLaylo() {
      setIsLoading(true);
      try {
        const layloResponse = await fetch("/api/laylo");
        if (layloResponse.ok) {
          const layloData = await layloResponse.json();
          if (layloData.laylo) {
            setLaylo(layloData.laylo);
            setLayloApiKey(layloData.laylo.apiKey || "");
          }
        } else if (layloResponse.status !== 404) {
          console.error("Failed to fetch Laylo integration");
        }
      } catch (error) {
        console.error("Failed to fetch Laylo integration:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchLaylo();
  }, []);

  const handleCreateLaylo = async () => {
    if (!layloApiKey.trim()) {
      toast.error("API key is required");
      return;
    }

    setIsCreatingLaylo(true);
    try {
      const response = await fetch("/api/laylo", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ apiKey: layloApiKey }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.laylo) {
          setLaylo(data.laylo);
          setLayloApiKey(data.laylo.apiKey || "");
          toast.success("Laylo integration created successfully");
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to create Laylo integration");
      }
    } catch (error) {
      console.error("Error creating Laylo integration:", error);
      toast.error(error instanceof Error ? error.message : "Failed to create Laylo integration");
    } finally {
      setIsCreatingLaylo(false);
    }
  };

  const handleSaveLaylo = async () => {
    if (!laylo) return;

    setIsSavingLaylo(true);
    try {
      const response = await fetch("/api/laylo", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ apiKey: layloApiKey }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.laylo) {
          setLaylo(data.laylo);
          toast.success("Laylo settings saved successfully");
        }
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to save Laylo settings");
      }
    } catch (error) {
      console.error("Error saving Laylo settings:", error);
      toast.error(error instanceof Error ? error.message : "Failed to save Laylo settings");
    } finally {
      setIsSavingLaylo(false);
    }
  };

  const handleDeleteLaylo = async () => {
    if (!laylo) return;

    if (!confirm("Are you sure you want to delete your Laylo integration?")) {
      return;
    }

    setIsDeletingLaylo(true);
    try {
      const response = await fetch(`/api/laylo/${laylo._id}`, {
        method: "DELETE",
      });

      if (response.ok) {
        setLaylo(null);
        setLayloApiKey("");
        toast.success("Laylo integration deleted successfully");
      } else {
        const error = await response.json();
        throw new Error(error.error || "Failed to delete Laylo integration");
      }
    } catch (error) {
      console.error("Error deleting Laylo integration:", error);
      toast.error(error instanceof Error ? error.message : "Failed to delete Laylo integration");
    } finally {
      setIsDeletingLaylo(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[40vh]">
        <Loader2 className="animate-spin h-12 w-12 text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div className="border rounded-lg p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1 flex-1">
            <h3 className="text-lg font-semibold">Laylo</h3>
            <p className="text-sm text-muted-foreground">
              Connect your Laylo account for drop notifications and fan engagement
            </p>
            {laylo && (
              <div className="mt-2 space-y-1">
                <p className="text-sm text-green-600">Connected</p>
              </div>
            )}
          </div>
          {laylo && (
            <Button onClick={handleDeleteLaylo} disabled={isDeletingLaylo} variant="destructive">
              {isDeletingLaylo ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </Button>
          )}
        </div>
        <div className="space-y-2 pt-4 border-t">
          <div className="space-y-2">
            <Label htmlFor="layloApiKey">API Key</Label>
            <div className="relative">
              <Input
                id="layloApiKey"
                type={showLayloApiKey ? "text" : "password"}
                value={layloApiKey}
                onChange={(e) => setLayloApiKey(e.target.value)}
                placeholder="Enter Laylo API key"
                className="pr-10"
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowLayloApiKey(!showLayloApiKey)}
              >
                {showLayloApiKey ? (
                  <EyeOff className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <Eye className="h-4 w-4 text-muted-foreground" />
                )}
              </Button>
            </div>
          </div>
          {!laylo ? (
            <Button onClick={handleCreateLaylo} disabled={isCreatingLaylo}>
              {isCreatingLaylo ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                "Connect"
              )}
            </Button>
          ) : (
            <Button onClick={handleSaveLaylo} disabled={isSavingLaylo}>
              {isSavingLaylo ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
